import React, { useState, useEffect } from 'react';
import { Drawer, Form, InputNumber, Switch, Button, Space, Alert, Divider, message, Typography, Select } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { uldStockAlertService } from '../../../../api/enhancedApi';
import { readApiErrorMessage } from '../readApiError';

const EMAIL_PATTERN = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const normalizeEmails = (value) => (Array.isArray(value) ? value.map((email) => String(email).trim()).filter(Boolean) : []);

const { Text } = Typography;

const UldAlertConfigDrawer = ({ visible, onClose, airportData, onSuccess }) => {
    const [form] = Form.useForm();
    const [submitting, setSubmitting] = useState(false);

    // 初始化表单数据
    useEffect(() => {
        if (visible && airportData) {
            // recipients 现在直接存储邮箱地址数组
            form.setFieldsValue({
                min_stock_threshold: airportData.min_stock_threshold ?? 0,
                is_email_enabled: airportData.is_email_enabled ?? false,
                recipients: airportData.recipients || [],
            });
        } else {
            form.resetFields();
        }
    }, [visible, airportData, form]);

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setSubmitting(true);

            const dataToSubmit = {
                ...values,
                min_stock_threshold: values.min_stock_threshold ?? 0,
            };
            if (Array.isArray(values.recipients)) {
                dataToSubmit.recipients = normalizeEmails(values.recipients);
            } else {
                delete dataToSubmit.recipients;
            }

            if (airportData?.config_id) {
                await uldStockAlertService.updateConfig({
                    id: airportData.config_id,
                    data: dataToSubmit
                });
                message.success('配置更新成功');
            } else {
                const res = await uldStockAlertService.addMonitor({
                    airport_iata: airportData.airport_iata
                });
                const configId = res?.data?.id || res?.id;
                if (!configId) {
                    throw new Error('添加监控失败：未能获取配置ID');
                }
                try {
                    await uldStockAlertService.updateConfig({
                        id: configId,
                        data: dataToSubmit
                    });
                } catch (error) {
                    message.error(`站点已加入列表，但预警设置没有保存：${readApiErrorMessage(error, '请重新打开配置再保存')}`);
                    onSuccess();
                    onClose();
                    return;
                }
                message.success('监控已添加并配置成功');
            }

            onSuccess();
            onClose();
        } catch (error) {
            if (error?.errorFields) return;
            console.error('保存配置失败:', error);
            message.error(readApiErrorMessage(error, error?.message || '保存配置失败，请重试'));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Drawer
            title={`库存预警配置 - ${airportData?.airport_iata || ''}`}
            width={500}
            onClose={onClose}
            open={visible}
            extra={
                <Space>
                    <Button onClick={onClose}>取消</Button>
                    <Button type="primary" onClick={handleSubmit} loading={submitting}>
                        保存
                    </Button>
                </Space>
            }
        >
            <Alert
                message="配置说明"
                description="设置该站点的最低库存阈值。当实际可用库存低于该值时，系统将触发报警。如果开启了邮件预警，系统将向指定收件人发送通知邮件。"
                type="info"
                showIcon
                style={{ marginBottom: 24 }}
            />

            <Form
                form={form}
                layout="vertical"
                initialValues={{ min_stock_threshold: 0, is_email_enabled: false }}
            >
                <Divider orientation="left">阈值设置</Divider>
                
                <Form.Item
                    name="min_stock_threshold"
                    label="最低库存阈值 (个)"
                    dependencies={['is_email_enabled']}
                    help="设为 0 表示不报警。要发邮件时必须大于 0。"
                    rules={[
                        ({ getFieldValue }) => ({
                            validator(_, value) {
                                if (getFieldValue('is_email_enabled') && !(Number(value) > 0)) {
                                    return Promise.reject(new Error('开启邮件预警时，最低库存必须大于 0'));
                                }
                                return Promise.resolve();
                            },
                        }),
                    ]}
                >
                    <InputNumber min={0} max={10000} precision={0} style={{ width: '100%' }} />
                </Form.Item>

                <Divider orientation="left">预警通知</Divider>

                <Form.Item
                    name="is_email_enabled"
                    label="开启邮件预警"
                    valuePropName="checked"
                >
                    <Switch />
                </Form.Item>

                <Form.Item
                    noStyle
                    shouldUpdate={(prev, current) => prev.is_email_enabled !== current.is_email_enabled}
                >
                    {({ getFieldValue }) => 
                        getFieldValue('is_email_enabled') && (
                            <Form.Item
                                name="recipients"
                                label="预警收件人"
                                rules={[
                                    { required: true, message: '开启预警后必须配置收件人' },
                                    {
                                        validator: (_, value) => {
                                            const emails = normalizeEmails(value);
                                            if (emails.length === 0) {
                                                return Promise.reject('至少添加一个收件人邮箱');
                                            }
                                            const invalidEmails = emails.filter((email) => !EMAIL_PATTERN.test(email));
                                            if (invalidEmails.length > 0) {
                                                return Promise.reject(`以下邮箱格式不正确: ${invalidEmails.join(', ')}`);
                                            }
                                            return Promise.resolve();
                                        }
                                    }
                                ]}
                                help="请输入收件人邮箱地址，可添加多个，回车或逗号分隔"
                            >
                                <Select
                                    mode="tags"
                                    style={{ width: '100%' }}
                                    placeholder="输入邮箱地址后按回车添加，例如: admin@example.com"
                                    tokenSeparators={[',', '，']}
                                    notFoundContent={null}
                                />
                            </Form.Item>
                        )
                    }
                </Form.Item>
                
                {/* 模板预览 (只读) */}
                <div style={{ marginTop: 24, padding: 16, background: '#f5f5f5', borderRadius: 4 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        <InfoCircleOutlined style={{ marginRight: 4 }} />
                        将使用系统统一模板发送邮件：
                    </Text>
                    <div style={{ marginTop: 8, fontSize: 13, color: '#666' }}>
                        <strong>主题:</strong> [ULD库存预警] {airportData?.airport_iata} 站点库存不足<br/>
                        <strong>正文:</strong> 系统监测到 {airportData?.airport_iata} 站点的当前可用ULD库存为 {airportData?.current_stock || 0}，已低于设定的最低阈值。请相关人员及时处理。
                    </div>
                </div>

            </Form>
        </Drawer>
    );
};

export default UldAlertConfigDrawer;

